import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Palette as PaletteIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface RelatorioVendas {
  totalVendas: number;
  totalOrcamentos: number;
  ticketMedio: number;
  vendsPorMes: { mes: string; valor: number }[];
}

interface RelatorioFinanceiro {
  totalEntradas: number;
  totalSaidas: number;
  saldo: number;
  entradaPorCategoria: { categoria: string; valor: number }[];
  saidaPorCategoria: { categoria: string; valor: number }[];
}

interface RelatorioClientes {
  totalClientes: number;
  clientesAtivos: number;
  topClientes: { nome: string; totalGasto: number }[];
}

const Relatorios: React.FC = () => {
  const [tipoRelatorio, setTipoRelatorio] = useState<'vendas' | 'financeiro' | 'clientes' | 'formulas'>('vendas');
  const [dataInicio, setDataInicio] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [relatorioData, setRelatorioData] = useState<any>(null);


  const handleGerarRelatorio = async () => {
    setLoading(true);
    try {
      let data;
      switch (tipoRelatorio) {
        case 'vendas':
          data = await gerarRelatorioVendas();
          break;
        case 'financeiro':
          data = await gerarRelatorioFinanceiro();
          break;
        case 'clientes':
          data = await gerarRelatorioClientes();
          break;
        case 'formulas':
          data = await gerarRelatorioFormulas();
          break;
      }
      setRelatorioData(data);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorioVendas = async (): Promise<RelatorioVendas> => {
    try {
      const orcamentos = await apiService.getOrcamentos();
      const orcamentosArray = orcamentos || [];
      const orcamentosFiltrados = orcamentosArray.filter(o => {
        const dataOrc = new Date(o.dataOrcamento);
        const inicio = new Date(dataInicio);
        const fim = new Date(dataFim);
        return dataOrc >= inicio && dataOrc <= fim;
      });

      const totalVendas = orcamentosFiltrados
        .filter(o => o.status === 'pago')
        .reduce((sum, o) => sum + o.valorTotal, 0);

      const totalOrcamentos = orcamentosFiltrados.length;
      const ticketMedio = totalOrcamentos > 0 ? totalVendas / totalOrcamentos : 0;

      // Simulação de vendas por mês
      const vendsPorMes = [
        { mes: 'Jan', valor: 15000 },
        { mes: 'Fev', valor: 18000 },
        { mes: 'Mar', valor: 22000 },
        { mes: 'Abr', valor: 19000 },
        { mes: 'Mai', valor: 25000 },
        { mes: 'Jun', valor: 28000 },
      ];

      return {
        totalVendas,
        totalOrcamentos,
        ticketMedio,
        vendsPorMes,
      };
    } catch (error) {
      console.error('Erro ao gerar relatório de vendas:', error);
      return {
        totalVendas: 0,
        totalOrcamentos: 0,
        ticketMedio: 0,
        vendsPorMes: [],
      };
    }
  };

  const gerarRelatorioFinanceiro = async (): Promise<RelatorioFinanceiro> => {
    try {
      const movimentos = await apiService.getMovimentos();
      const movimentosArray = movimentos || [];
      const movimentosFiltrados = movimentosArray.filter(m => {
        const dataMovimento = new Date(m.data);
        const inicio = new Date(dataInicio);
        const fim = new Date(dataFim);
        return dataMovimento >= inicio && dataMovimento <= fim;
      });

      const totalEntradas = movimentosFiltrados
        .filter(m => m.tipo === 'entrada')
        .reduce((sum, m) => sum + m.valor, 0);

      const totalSaidas = movimentosFiltrados
        .filter(m => m.tipo === 'saida')
        .reduce((sum, m) => sum + m.valor, 0);

      const saldo = totalEntradas - totalSaidas;

      // Agrupamento por categoria
      const entradaPorCategoria = movimentosFiltrados
        .filter(m => m.tipo === 'entrada')
        .reduce((acc, m) => {
          const existing = acc.find(item => item.categoria === m.categoria);
          if (existing) {
            existing.valor += m.valor;
          } else {
            acc.push({ categoria: m.categoria, valor: m.valor });
          }
          return acc;
        }, [] as { categoria: string; valor: number }[]);

      const saidaPorCategoria = movimentosFiltrados
        .filter(m => m.tipo === 'saida')
        .reduce((acc, m) => {
          const existing = acc.find(item => item.categoria === m.categoria);
          if (existing) {
            existing.valor += m.valor;
          } else {
            acc.push({ categoria: m.categoria, valor: m.valor });
          }
          return acc;
        }, [] as { categoria: string; valor: number }[]);

      return {
        totalEntradas,
        totalSaidas,
        saldo,
        entradaPorCategoria: entradaPorCategoria || [],
        saidaPorCategoria: saidaPorCategoria || [],
      };
    } catch (error) {
      console.error('Erro ao gerar relatório financeiro:', error);
      return {
        totalEntradas: 0,
        totalSaidas: 0,
        saldo: 0,
        entradaPorCategoria: [],
        saidaPorCategoria: [],
      };
    }
  };

  const gerarRelatorioClientes = async (): Promise<RelatorioClientes> => {
    try {
      const [clientes, orcamentos] = await Promise.all([
        apiService.getClientes(),
        apiService.getOrcamentos(),
      ]);

      const clientesArray = clientes || [];
      const orcamentosArray = orcamentos || [];

      const totalClientes = clientesArray.length;
      const clientesAtivos = clientesArray.filter(c => {
        return orcamentosArray.some(o => o.clienteId === c.id);
      }).length;

      // Top clientes por valor gasto
      const topClientes = clientesArray.map(cliente => {
        const totalGasto = orcamentosArray
          .filter(o => o.clienteId === cliente.id && o.status === 'pago')
          .reduce((sum, o) => sum + o.valorTotal, 0);
        return { nome: cliente.nome, totalGasto };
      })
      .filter(c => c.totalGasto > 0)
      .sort((a, b) => b.totalGasto - a.totalGasto)
      .slice(0, 10);

      return {
        totalClientes,
        clientesAtivos,
        topClientes: topClientes || [],
      };
    } catch (error) {
      console.error('Erro ao gerar relatório de clientes:', error);
      return {
        totalClientes: 0,
        clientesAtivos: 0,
        topClientes: [],
      };
    }
  };

  const gerarRelatorioFormulas = async () => {
    try {
      const formulas = await apiService.getFormulas();
      const formulasArray = formulas || [];

      return {
        totalFormulas: formulasArray.length,
        formulasRecentes: formulasArray
          .sort((a, b) => new Date(b.dataCriacao).getTime() - new Date(a.dataCriacao).getTime())
          .slice(0, 10),
        pigmentosMaisUsados: [
          { nome: 'Azul Cobalto', vezes: 15 },
          { nome: 'Vermelho Carmim', vezes: 12 },
          { nome: 'Amarelo Cádmio', vezes: 10 },
          { nome: 'Verde Esmeralda', vezes: 8 },
          { nome: 'Preto Marfim', vezes: 6 },
        ],
      };
    } catch (error) {
      console.error('Erro ao gerar relatório de fórmulas:', error);
      return {
        totalFormulas: 0,
        formulasRecentes: [],
        pigmentosMaisUsados: [],
      };
    }
  };

  const handleExportarPDF = async () => {
    const element = document.getElementById('relatorio-content');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`relatorio-${tipoRelatorio}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const renderRelatorioVendas = (data: RelatorioVendas) => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" color="success.main">
                {formatCurrency(data.totalVendas)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Vendas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" color="primary.main">
                {data.totalOrcamentos}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Orçamentos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h5" color="info.main">
                {formatCurrency(data.ticketMedio)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ticket Médio
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Vendas por Mês
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Mês</TableCell>
                <TableCell align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.vendsPorMes && data.vendsPorMes.length > 0 ? (
                data.vendsPorMes.map((item) => (
                  <TableRow key={item.mes}>
                    <TableCell>{item.mes}</TableCell>
                    <TableCell align="right">{formatCurrency(item.valor)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    Nenhum dado encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  const renderRelatorioFinanceiro = (data: RelatorioFinanceiro) => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" color="success.main">
                {formatCurrency(data.totalEntradas)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Entradas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'error.main', mb: 1, transform: 'rotate(180deg)' }} />
              <Typography variant="h5" color="error.main">
                {formatCurrency(data.totalSaidas)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Saídas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssessmentIcon
                sx={{
                  fontSize: 40,
                  color: data.saldo >= 0 ? 'success.main' : 'error.main',
                  mb: 1
                }}
              />
              <Typography
                variant="h5"
                color={data.saldo >= 0 ? 'success.main' : 'error.main'}
              >
                {formatCurrency(data.saldo)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Saldo
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Entradas por Categoria
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.entradaPorCategoria && data.entradaPorCategoria.length > 0 ? (
                    data.entradaPorCategoria.map((item) => (
                      <TableRow key={item.categoria}>
                        <TableCell>{item.categoria}</TableCell>
                        <TableCell align="right">{formatCurrency(item.valor)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Nenhuma entrada encontrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Saídas por Categoria
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.saidaPorCategoria && data.saidaPorCategoria.length > 0 ? (
                    data.saidaPorCategoria.map((item) => (
                      <TableRow key={item.categoria}>
                        <TableCell>{item.categoria}</TableCell>
                        <TableCell align="right">{formatCurrency(item.valor)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Nenhuma saída encontrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderRelatorioClientes = (data: RelatorioClientes) => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" color="primary.main">
                {data.totalClientes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Clientes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" color="success.main">
                {data.clientesAtivos}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Clientes Ativos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Top Clientes por Valor Gasto
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Total Gasto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.topClientes && data.topClientes.length > 0 ? (
                data.topClientes.map((cliente, index) => (
                  <TableRow key={index}>
                    <TableCell>{cliente.nome}</TableCell>
                    <TableCell align="right">{formatCurrency(cliente.totalGasto)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    Nenhum cliente encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  const renderRelatorioFormulas = (data: any) => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PaletteIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" color="primary.main">
                {data.totalFormulas}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Fórmulas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PaletteIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h5" color="success.main">
                {data.formulasRecentes.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fórmulas Recentes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Fórmulas Recentes
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.formulasRecentes && data.formulasRecentes.length > 0 ? (
                    data.formulasRecentes.slice(0, 5).map((formula: any) => (
                      <TableRow key={formula.id}>
                        <TableCell>{formula.nome}</TableCell>
                        <TableCell>{formatDate(formula.dataCriacao)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Nenhuma fórmula encontrada
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pigmentos Mais Usados
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Pigmento</TableCell>
                    <TableCell align="right">Vezes Usado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.pigmentosMaisUsados && data.pigmentosMaisUsados.length > 0 ? (
                    data.pigmentosMaisUsados.map((pigmento: any) => (
                      <TableRow key={pigmento.nome}>
                        <TableCell>{pigmento.nome}</TableCell>
                        <TableCell align="right">{pigmento.vezes}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Nenhum pigmento encontrado
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 3 }}
      >
        Relatórios
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Relatório</InputLabel>
              <Select
                value={tipoRelatorio}
                label="Tipo de Relatório"
                onChange={(e) => setTipoRelatorio(e.target.value as any)}
              >
                <MenuItem value="vendas">Vendas</MenuItem>
                <MenuItem value="financeiro">Financeiro</MenuItem>
                <MenuItem value="clientes">Clientes</MenuItem>
                <MenuItem value="formulas">Fórmulas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3, md: 2 }}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleGerarRelatorio}
              disabled={loading}
              startIcon={<AssessmentIcon />}
            >
              {loading ? 'Gerando...' : 'Gerar Relatório'}
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleExportarPDF}
              disabled={!relatorioData}
              startIcon={<PdfIcon />}
            >
              Exportar PDF
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {relatorioData && (
        <Paper sx={{ p: 3 }} id="relatorio-content">
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Brastec Tinturaria
            </Typography>
            <Typography variant="h6" gutterBottom>
              Relatório de {tipoRelatorio.charAt(0).toUpperCase() + tipoRelatorio.slice(1)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Período: {formatDate(dataInicio)} a {formatDate(dataFim)}
            </Typography>
            <Divider sx={{ my: 2 }} />
          </Box>

          {tipoRelatorio === 'vendas' && renderRelatorioVendas(relatorioData)}
          {tipoRelatorio === 'financeiro' && renderRelatorioFinanceiro(relatorioData)}
          {tipoRelatorio === 'clientes' && renderRelatorioClientes(relatorioData)}
          {tipoRelatorio === 'formulas' && renderRelatorioFormulas(relatorioData)}
        </Paper>
      )}
    </Box>
  );
};

export default Relatorios;